/* eslint-disable no-param-reassign,react/jsx-props-no-spreading */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button, Row, Col, Table, Space, Tooltip } from "antd";
import "../../styles/filters-form.less";
import "./Payments.less";
import { jwtDecode } from "jwt-decode";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../..";
import {
  toDisplayFormat,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
} from "../../lib/utils/formatDate";
import AuthService from "../../services/AuthService";
import { formatDate, getAccountFullName, round } from "../../lib/utils";
import { exportToXLS } from "../../store/slices/xlsExport";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_PAYMENTS_WRITE, PAYMENTS_REG_PAYMENT } from "../../lib/const/permissions";
import SpanNumberFormat from "../common/SpanNumberFormat";
import { ReactComponent as PrepaidBillIcon } from "../../img/payment_icons/prepaid-bill.svg";
import { ReactComponent as PostpaidBillIcon } from "../../img/payment_icons/postpaid-bill.svg";
import { ReactComponent as BillFactureIcon } from "../../img/payment_icons/bill-facture.svg";
import { ReactComponent as InvoiceIcon } from "../../img/payment_icons/invoice.svg";
import ToggleColumns from "../ToggleableVersionsTable/ToggleColumns";
import { PaymentsSearchFilters } from "./PaymentsSearchFilters/PaymentsSearchFilters";
import { PaymentRegisterModal } from "../AccountPayments/PaymentRegisterModal/PaymentRegisterModal";
import { useLoading } from "../../lib/hooks/useLoading";

const Payments = ({
  isLoadData,
  payments,
  paymentsLoading,
  handleSearchPayments,
  resetSearchPayments,
  paymentStatuses,
  paymentsSearchParams,
  form,
}) => {
  const dispatch = useAppDispatch();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { companyIdentityAttribute } = useAppSelector((state) => state.tenants.tenantProperties);
  const { paymentSourceTypes } = useAppSelector((state) => state.rtkDictionaries);
  const [filtersCollapseState, setFiltersCollapsState] = useState("unfolded");

  const resetSearchPaymentsAndResetSelectedRows = () => {
    setSelectedRowKeys([]);
    resetSearchPayments();
  };

  const getTableHeight = () => (filtersCollapseState === "unfolded" ? 400 : 560);

  const { isLoading, startLoading, stopLoading } = useLoading();

  const renderFooter = () => {
    const handleExport = () => {
      startLoading();
      const selectedPayments =
        isLoadData &&
        checkIsLoaded(paymentsLoading) &&
        payments?.content?.filter((item) => selectedRowKeys.includes(item.id));
      const { display_name: author } = jwtDecode(AuthService.getCurrentUser());
      const exportedData = {
        fileName: `Платежи_${formatDate.toDisplayFormat(moment())}`,
        title: "Выгрузка данных в формате xls",
        description: "Выгрузка данных",
        author,
        header: [
          { name: "client", title: "Клиент", type: "String" },
          { name: "agreementNumber", title: "Номер договора", type: "String" },
          { name: "amount", title: "Сумма", type: "Double" },
          { name: "paymentDate", title: "Дата платежа", type: "LocalDate" },
          { name: "dateUpdate", title: "Дата обработки", type: "LocalDate" },
          { name: "goal", title: "Цель платежа", type: "String" },
        ],
        data: selectedPayments.map((item) => {
          const goals = item?.goals && item?.goals.map((item2) => item2.goalId).join(", ");

          const clientOrgName = item?.agreement?.account?.organization?.fullName;
          const individual = item?.agreement?.account?.individual;
          const clientIndividualName = individual
            ? [individual.lastName, individual.firstName, individual.middleName]
              .filter((el) => Boolean(el))
              .join(" ")
            : "";

          return {
            client:
              clientOrgName || clientIndividualName || String(item?.agreement?.account?.id) || "",
            agreementNumber: item?.agreement?.agreementNumber,
            amount: round(item?.amount),
            paymentDate: item?.paymentDate ? toDisplayFormat(item?.paymentDate) : "",
            dateUpdate: item?.confirmDate ? toDisplayFormat(item?.confirmDate) : "",
            goal: goals,
          };
        }),
      };

      dispatch(exportToXLS(exportedData)).finally(stopLoading);
    };

    return (
      <Space className="buttons-container">
        {hasAccess(MENU_PAYMENTS_WRITE) && (
          <Button
            type="primary"
            disabled={selectedRowKeys.length === 0}
            loading={isLoading}
            onClick={handleExport}
          >
            Выгрузить в XLS
          </Button>
        )}
      </Space>
    );
  };

  let tableData;

  if (checkIsLoaded(paymentsLoading)) {
    tableData =
      isLoadData &&
      payments &&
      payments?.content?.map((payment) => ({
        key: payment.id,
        id: payment.id,
        client: payment?.agreement?.account ? getAccountFullName(payment?.agreement?.account) : "",
        agreementNumber: payment?.agreement?.agreementNumber,
        agreementId: payment?.agreement?.id,
        amount: round(payment?.amount),
        paymentDate: payment.paymentDate ? toDisplayFormat(payment.paymentDate) : "",
        confirmDate: payment.confirmDate ? toDisplayFormat(payment.confirmDate) : "",
        goal: payment?.goals,
        paymentStatusName:
          paymentStatuses.find(({ id }) => String(id) === String(payment.paymentStatusId))?.name ||
          "",
        reserveLeftAmount: payment?.extra?.reserveLeftAmount,
        usedAmount: payment?.extra?.usedAmount,
        availableAmount: payment?.extra?.availableAmount,
        sourceTypeName: paymentSourceTypes.find(({ id }) => id === payment.sourceTypeId)?.name,
        accountId: payment?.agreement?.account?.id,
        documentNumber: payment?.info?.documentNumber,
        purpose: payment?.info?.purpose,
      }));
  }

  const getGoalIcon = (goalTypeId) => {
    const icons = {
      1: [<PrepaidBillIcon />, "Счет на предоплату"],
      2: [<PostpaidBillIcon />, "Счет на постоплату"],
      3: [<BillFactureIcon />, "Счет-фактура"],
      4: [<InvoiceIcon />, "Инвойс"],
      // Временно добавлены для отображения значков (картинку нарисуют позже)
      5: [<InvoiceIcon />, "Предложение"],
      6: [<InvoiceIcon />, "Продукт"],
    };

    const [icon, title] = icons[goalTypeId];
    return (
      <div style={{ marginRight: "5px", width: "24px", height: "24px" }}>
        <Tooltip title={title}>{icon}</Tooltip>
      </div>
    );
  };

  const columnsWidths = {
    id: "85px",
    client: "140px",
    agreementNumber: "120px",
    amount: "100px",
    reserveLeftAmount: "100px",
    usedAmount: "110px",
    availableAmount: "100px",
    paymentDate: "85px",
    documentNumber: "120px",
    purpose: "120px",
    paymentStatusName: "100px",
    confirmDate: "85px",
    goal: "140px",
    sourceTypeName: "140px",
  };

  const [columns, setColumns] = useState([
    {
      title: "ID платежа",
      dataIndex: "id",
      key: "id",
      hidden: true,
      width: columnsWidths.id,
    },
    {
      title: "Клиент",
      dataIndex: "client",
      key: "client",
      hidden: false,
      width: columnsWidths.client,
    },
    {
      title: "Номер договора",
      dataIndex: "agreementNumber",
      key: "agreementNumber",
      hidden: false,
      width: columnsWidths.agreementNumber,
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      key: "amount",
      hidden: false,
      render: (amount) => SpanNumberFormat({ amount }),
      width: columnsWidths.amount,
    },
    {
      title: "Резерв",
      dataIndex: "reserveLeftAmount",
      key: "reserveLeftAmount",
      hidden: true,
      render: (reserveLeftAmount) => SpanNumberFormat({ amount: reserveLeftAmount }),
      width: columnsWidths.reserveLeftAmount,
    },
    {
      title: "Использовано",
      dataIndex: "usedAmount",
      key: "usedAmount",
      hidden: true,
      render: (usedAmount) => SpanNumberFormat({ amount: usedAmount }),
      width: columnsWidths.usedAmount,
    },
    {
      title: "Остаток",
      dataIndex: "availableAmount",
      key: "availableAmount",
      hidden: false,
      render: (availableAmount, { accountId, client, agreementId }) => (
        <Link target="_blank" to={`/accounts/${accountId}/bills/search?agreementId=${agreementId}`}>
          <Tooltip title={`Счета ${client}`}>
            {SpanNumberFormat({ amount: availableAmount })}
          </Tooltip>
        </Link>
      ),
      width: columnsWidths.availableAmount,
    },
    {
      title: "Дата платежа",
      dataIndex: "paymentDate",
      key: "paymentDate",
      hidden: false,
      width: columnsWidths.paymentDate,
    },
    {
      title: "Номер документа",
      dataIndex: "documentNumber",
      key: "documentNumber",
      hidden: true,
      // width: "7%",
      width: columnsWidths.documentNumber,
    },
    {
      title: "Назначение",
      dataIndex: "purpose",
      key: "purpose",
      hidden: true,
      // width: "12%",
      width: columnsWidths.purpose,
    },
    {
      title: "Статус",
      dataIndex: "paymentStatusName",
      key: "paymentStatusName",
      hidden: false,
      width: columnsWidths.paymentStatusName,
    },
    {
      title: "Дата обработки",
      dataIndex: "confirmDate",
      key: "confirmDate",
      hidden: false,
      width: columnsWidths.confirmDate,
    },
    {
      title: "Цель платежа",
      dataIndex: "goal",
      key: "goal",
      hidden: false,
      render: (goals) => (
        <>
          {goals &&
            goals.map((item) => (
              <div
                className="payments__inner-cell"
                key={item.id}
                style={{ wordWrap: "break-word" }}
              >
                <div style={{ display: "flex" }}>
                  {getGoalIcon(item.goalTypeId)}
                  <div>
                    {/*	Для Типа цели goalTypeId = 3 (счет-фактура) вместо goalId выводим goals: invoices: extNum 
                Если в массиве invoices несколько элементов, то берем из первого. */}
                    <span>{item.goalTypeId === 3 ? item.invoices?.[0]?.extNum : item.goalId}</span>
                  </div>
                </div>
              </div>
            ))}
        </>
      ),
      width: columnsWidths.goal,
    },
    {
      title: "Источник платежа",
      dataIndex: "sourceTypeName",
      key: "sourceTypeName",
      hidden: false,
      width: columnsWidths.sourceTypeName,
    },
  ]);

  const onSearch = (values) => {
    if (values) {
      if (values["organization.property.stringValue"]) {
        values["organization.property.typeId"] =
          companyIdentityAttribute && companyIdentityAttribute;
      }
      values.unpaged = true;
      values.paymentDateFrom = toHttpFormatStartOfDay(values.datePay[0]);
      values.paymentDateTo = toHttpFormatEndOfDay(values.datePay[1]);
      values.confirmDateFrom =
        values.dateConfirm && values.dateConfirm[0]
          ? toHttpFormatStartOfDay(values.dateConfirm[0])
          : "";
      values.confirmDateTo =
        values.dateConfirm && values.dateConfirm[1]
          ? toHttpFormatEndOfDay(values.dateConfirm[1])
          : "";
      values.datePay = "";
      values.dateConfirm = "";
      if (values?.paymentIds) values.paymentIds.split(",");
    }
    setSelectedRowKeys([]);
    handleSearchPayments(values);
  };

  const [agreementForRebase, setAgreementForRebase] = useState();
  const [visibleRegisterPaymentModal, setVisibleRegisterPaymentModal] = useState(false);

  return (
    <>
      <div className="inner-row" style={{ height: "50px" }}>
        <Row justify="space-between" align="middle">
          <Col>
            <h1>Массовые операции по работе с платежами</h1>
          </Col>
          <Col>
            {hasAccess(PAYMENTS_REG_PAYMENT) && (
              <Button type="primary" onClick={() => setVisibleRegisterPaymentModal(true)}>
                Зарегистрировать платеж
              </Button>
            )}
          </Col>
        </Row>
      </div>
      <PaymentsSearchFilters
        form={form}
        defaultSearchParams={paymentsSearchParams}
        onSearch={onSearch}
        filtersCollapseState={filtersCollapseState}
        setFiltersCollapsState={setFiltersCollapsState}
        resetSearchPaymentsAndResetSelectedRows={resetSearchPaymentsAndResetSelectedRows}
      />
      <div style={{ marginLeft: "97%", marginTop: "7px" }}>
        <ToggleColumns tableName="payments" columns={columns} setColumns={setColumns} />
      </div>
      <Table
        size="small"
        columns={[...columns].filter((item) => !item.hidden)}
        dataSource={tableData}
        loading={checkIsLoading(paymentsLoading)}
        pagination={false}
        scroll={{ y: getTableHeight(), x: true }}
        rowSelection={{
          selectedRowKeys,
          onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
          },
        }}
      />
      <Row justify="center">{renderFooter()}</Row>
      <PaymentRegisterModal
        visibleRegisterPaymentModal={visibleRegisterPaymentModal}
        setVisibleRegisterPaymentModal={setVisibleRegisterPaymentModal}
        agreementForRebase={agreementForRebase}
        setAgreementForRebase={setAgreementForRebase}
        isAccount={false}
        updatePayments={form.submit}
      />
    </>
  );
};

export default Payments;
